import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button, Chip, Icon, RoleChip, Skeleton, Spinner } from '@w7/ui'
import { Amount, Money } from '@/app/bits'
import { useChildren } from '@/app/queries'
import { round2 } from '@/lib/book'
import { EMPTY, count, money } from '@/lib/format'
import { ROLE_LABEL, ROLE_SHORT, isAgent, type Account } from '@/data/types'
import { STATUS, UsageCell } from './DownlineTable'
import { showMemberListExposure } from './memberListExposure'

/**
 * The whole downline, as a real treegrid.
 *
 * A standalone `<table role="treegrid">` rather than `DataTable`: tree semantics
 * (levels, expansion, roving focus) do not belong bolted onto a component eight
 * other screens use, and `DataTable`'s sort and selection machinery is
 * meaningless here — sorting a lazily-loaded subtree either reorders children
 * away from their parents or silently sorts one level.
 *
 * There are NO give/take controls on any row, and no Actions column at all.
 * Credit only ever moves between you and your DIRECT children, so a tree of
 * disabled buttons would be a hundred rows of the same explanation; the page
 * says it once, above the table.
 *
 * Each level is its own `useChildren` query, so re-expanding is instant off the
 * cache and the whole tree never loads at once. Recursion is per NODE (each row
 * component owns the query for its own children) rather than a flattened list
 * with `useQueries`, because that keeps every hook call static and lets a
 * collapse unmount exactly the subtree it closed.
 */

const COLUMNS = 7

interface TreeContextValue {
  expanded: Set<string>
  toggle: (id: string, open: boolean, username: string, childCount: number) => void
  tabbableId: string | null
  setFocusId: (id: string) => void
  highlightId: string | null
}

const TreeContext = createContext<TreeContextValue | null>(null)
const useTree = (): TreeContextValue => {
  const value = useContext(TreeContext)
  if (!value) throw new Error('Tree rows must render inside DownlineTree')
  return value
}

export interface DownlineTreeProps {
  /** Your direct children — the root level. */
  roots: Account[]
  loading: boolean
  expanded: Set<string>
  onExpandedChange: (id: string, open: boolean) => void
  /** "Show in tree" landing: scroll to, focus and briefly highlight this row. */
  landOn?: string
  empty: ReactNode
}

export function DownlineTree({
  roots,
  loading,
  expanded,
  onExpandedChange,
  landOn,
  empty,
}: DownlineTreeProps) {
  const navigate = useNavigate()
  const bodyRef = useRef<HTMLTableSectionElement>(null)
  const [focusId, setFocusId] = useState<string | null>(null)
  const [highlightId, setHighlightId] = useState<string | null>(null)
  const [announcement, setAnnouncement] = useState('')
  const landedRef = useRef<string | null>(null)

  const rowsOf = useCallback(
    (): HTMLTableRowElement[] =>
      Array.from(bodyRef.current?.querySelectorAll<HTMLTableRowElement>('tr[data-id]') ?? []),
    [],
  )

  const toggle = useCallback(
    (id: string, open: boolean, username: string, childCount: number) => {
      onExpandedChange(id, open)
      setAnnouncement(
        open
          ? `${username} expanded, ${childCount} ${childCount === 1 ? 'child' : 'children'}`
          : `${username} collapsed`,
      )
      if (!open) {
        // Collapsing unmounts everything beneath, which can take DOM focus with
        // it and leave no row tabbable at all. The head of the group you just
        // closed is the only defensible place for the cursor to land.
        setFocusId(id)
        if (bodyRef.current?.contains(document.activeElement)) {
          window.requestAnimationFrame(() => {
            rowsOf()
              .find((row) => row.dataset.id === id)
              ?.focus()
          })
        }
      }
    },
    [onExpandedChange, rowsOf],
  )

  /**
   * The landing from a match list's "Show in tree". The row only exists once
   * every ancestor above it has loaded, so this runs on each render until it
   * finds the row, then latches.
   */
  useEffect(() => {
    if (!landOn || landedRef.current === landOn) return
    const target = rowsOf().find((row) => row.dataset.id === landOn)
    if (!target) return
    landedRef.current = landOn
    target.scrollIntoView({ block: 'center' })
    target.focus()
    setHighlightId(landOn)
    const timer = window.setTimeout(() => setHighlightId(null), 2000)
    return () => window.clearTimeout(timer)
  })

  /**
   * Treegrid keys, read off the DOM rather than a mirrored list of visible rows.
   * The rows ARE the list — a second copy of it in state would go stale exactly
   * when a level finished loading.
   *
   * Deliberately NOT implemented, so nobody "fixes" it later: `*` (expand every
   * sibling) would fire an unbounded fan of listChildren calls, and type-ahead
   * can only search rows that happen to be loaded — ⌘K covers "find this
   * person" across the whole downline and does it honestly.
   */
  const onKeyDown = (event: KeyboardEvent<HTMLTableSectionElement>) => {
    const target = event.target as HTMLElement
    const current = target.closest?.('tr[data-id]') as HTMLTableRowElement | null
    if (!current) return

    const all = rowsOf()
    const index = all.indexOf(current)
    const id = current.dataset.id ?? ''
    const step = (to: HTMLTableRowElement | undefined) => {
      if (!to) return
      event.preventDefault()
      to.focus()
    }

    switch (event.key) {
      case 'ArrowDown':
        step(all[index + 1])
        return
      case 'ArrowUp':
        step(all[index - 1])
        return
      case 'Home':
        step(all[0])
        return
      case 'End':
        step(all[all.length - 1])
        return
      case 'ArrowRight': {
        if (current.dataset.expandable !== 'true') return
        event.preventDefault()
        if (current.getAttribute('aria-expanded') !== 'true') {
          toggle(id, true, current.dataset.username ?? id, Number(current.dataset.childcount ?? 0))
          return
        }
        // Already open. Step into the first child — but never while the level is
        // still loading, or focus lands on whatever renders next.
        if (current.getAttribute('aria-busy') === 'true') return
        const next = all[index + 1]
        if (next && next.dataset.parent === id) next.focus()
        return
      }
      case 'ArrowLeft': {
        event.preventDefault()
        if (current.getAttribute('aria-expanded') === 'true') {
          toggle(id, false, current.dataset.username ?? id, Number(current.dataset.childcount ?? 0))
          return
        }
        const parent = all.find((row) => row.dataset.id === current.dataset.parent)
        if (parent) parent.focus()
        return
      }
      case 'Enter':
      case ' ':
        // Space must be prevented or the page scrolls out from under the tree.
        event.preventDefault()
        navigate(`/member/${id}`)
        return
      default:
    }
  }

  if (!loading && roots.length === 0) return <div className="w7-tablewrap">{empty}</div>

  const tabbableId = focusId ?? roots[0]?.id ?? null

  return (
    <TreeContext.Provider value={{ expanded, toggle, tabbableId, setFocusId, highlightId }}>
      <div className="w7-tablewrap">
        <table className="w7-table w7-dtree" role="treegrid" aria-label="Your whole downline as a tree">
          <caption className="w7-sr">
            Your whole downline as a tree. Expand a row to load its direct children. Credit only
            moves between you and your direct children, so this view is read-only.
          </caption>
          <thead>
            <tr>
              <th scope="col" style={{ width: 300 }}>
                Agent or member
              </th>
              <th
                scope="col"
                style={{ textAlign: 'right' }}
                data-hide-below={900}
                title="The ceiling you set on what this account may hold. Their own figure, not a subtree total."
              >
                Credit limit
              </th>
              <th
                scope="col"
                style={{ textAlign: 'right' }}
                title="What this account itself holds. Their downline's balances are on their own rows."
              >
                Balance
              </th>
              <th
                scope="col"
                style={{ textAlign: 'right' }}
                data-hide-below={560}
                title="Across everything beneath this account, not just their own book."
              >
                Exposure
              </th>
              <th
                scope="col"
                style={{ textAlign: 'right' }}
                data-hide-below={720}
                title="This account's own position with its parent. Positive is credit the parent collects."
              >
                Settlement
              </th>
              <th
                scope="col"
                style={{ textAlign: 'right' }}
                data-hide-below={1080}
                title="Every member at or beneath this account."
              >
                Members
              </th>
              <th scope="col" data-hide-below={900}>
                Status
              </th>
            </tr>
          </thead>
          <tbody ref={bodyRef} onKeyDown={onKeyDown}>
            {loading
              ? Array.from({ length: 8 }, (_, i) => (
                  <tr key={`skeleton-${i}`} aria-hidden="true">
                    <td colSpan={COLUMNS}>
                      <Skeleton width="60%" />
                    </td>
                  </tr>
                ))
              : roots.map((row, index) => (
                  <TreeNode
                    key={row.id}
                    row={row}
                    depth={0}
                    posinset={index + 1}
                    setsize={roots.length}
                  />
                ))}
          </tbody>
        </table>
      </div>
      <span className="w7-sr" aria-live="polite">
        {announcement}
      </span>
    </TreeContext.Provider>
  )
}

/* ------------------------------------------------------------------ node -- */

function TreeNode({
  row,
  depth,
  posinset,
  setsize,
}: {
  row: Account
  depth: number
  posinset: number
  setsize: number
}) {
  const { expanded, toggle, tabbableId, setFocusId, highlightId } = useTree()
  const open = expanded.has(row.id)
  const hasChildren = row.childCount > 0
  // Disabled while collapsed: the cache keeps whatever was loaded, so
  // re-expanding is instant and costs no request.
  const children = useChildren(open && hasChildren ? row.id : undefined)
  const loaded = children.data

  return (
    <>
      <tr
        role="row"
        data-id={row.id}
        data-parent={row.parentId ?? ''}
        data-username={row.username}
        data-childcount={row.childCount}
        data-expandable={hasChildren ? 'true' : undefined}
        data-highlight={highlightId === row.id ? 'true' : undefined}
        aria-level={depth + 1}
        aria-posinset={posinset}
        aria-setsize={setsize}
        aria-expanded={hasChildren ? open : undefined}
        aria-busy={children.isLoading ? true : undefined}
        tabIndex={tabbableId === row.id ? 0 : -1}
        onFocus={() => setFocusId(row.id)}
        style={{ '--w7-depth': depth } as CSSProperties}
      >
        <td>
          <span className="w7-dtree-name">
            {hasChildren ? (
              /* A MOUSE TARGET ONLY. The row itself carries aria-expanded and
                 ArrowRight/ArrowLeft drive expansion, so a focusable button here
                 would announce the same state twice and add a tab stop per row. */
              <button
                type="button"
                className="w7-dtree-toggle"
                tabIndex={-1}
                aria-hidden="true"
                onClick={() => toggle(row.id, !open, row.username, row.childCount)}
              >
                {children.isLoading ? (
                  <Spinner size={13} />
                ) : (
                  <Icon name={open ? 'chevron-down' : 'chevron-right'} size={14} />
                )}
              </button>
            ) : (
              /* Leaves reserve the twisty's exact width, so usernames stay in
                 one column at every depth. */
              <span className="w7-dtree-spacer" aria-hidden="true" />
            )}
            <span className="w7-dtree-ident">
              <span className="w7-dtree-ident-top">
                {/* tabIndex -1: mouse-clickable, but not a second tab stop
                    inside a roving-tabindex row. Enter on the row opens it. */}
                <Link to={`/member/${row.id}`} className="w7-link" tabIndex={-1}>
                  {row.username}
                </Link>
                <RoleChip role={row.role} label={ROLE_SHORT[row.role]} />
              </span>
              <span className="w7-dtree-ident-sub">
                {ROLE_LABEL[row.role]} · {row.displayName}
              </span>
            </span>
          </span>
        </td>
        <td className="w7-mono" style={{ textAlign: 'right' }} data-hide-below={900}>
          <Amount value={row.creditLimit} currency={row.currencyType} alreadyDisplay />
        </td>
        <td className="w7-mono" style={{ textAlign: 'right' }}>
          <Amount value={row.balance} currency={row.currencyType} alreadyDisplay />
        </td>
        <td className="w7-mono" style={{ textAlign: 'right' }} data-hide-below={560}>
          {showMemberListExposure(row.role) ? (
            <UsageCell
              exposure={row.exposure}
              creditLimit={row.creditLimit}
              currency={row.currencyType}
            />
          ) : (
            <span className="w7-dim">NA</span>
          )}
        </td>
        <td className="w7-mono" style={{ textAlign: 'right' }} data-hide-below={720}>
          <Money value={row.settlement} signed currency={row.currencyType} alreadyDisplay />
        </td>
        <td className="w7-mono" style={{ textAlign: 'right' }} data-hide-below={1080}>
          {isAgent(row.role) ? count(row.memberCount) : EMPTY}
        </td>
        <td data-hide-below={900}>
          <Chip tone={STATUS[row.status].tone} size="sm">
            {STATUS[row.status].label}
          </Chip>
        </td>
      </tr>

      {open && hasChildren ? (
        <>
          {children.isLoading ? (
            <SubRow depth={depth + 1} hidden>
              <Skeleton width="40%" />
            </SubRow>
          ) : children.isError ? (
            /* One branch failing must never take the whole tree down, so this is
               a row with its own retry, not a page-level StateBlock. */
            <SubRow depth={depth + 1}>
              <Icon name="warning" size={13} />
              {(children.error as Error)?.message ?? 'That level could not be loaded'}
              <Button size="sm" variant="ghost" icon="refresh" onClick={() => children.refetch()}>
                Try again
              </Button>
            </SubRow>
          ) : loaded && loaded.length === 0 ? (
            <SubRow depth={depth + 1}>No direct downline.</SubRow>
          ) : loaded ? (
            <>
              {loaded.map((child, index) => (
                <TreeNode
                  key={child.id}
                  row={child}
                  depth={depth + 1}
                  posinset={index + 1}
                  setsize={loaded.length}
                />
              ))}
              {/*
                DIRECT children, and it says so. It is computed from the loaded
                direct children only — `listChildren` returns all of them
                unpaged, so this figure is exact. A collapsed node claims no
                rollup at all: a money figure that is right only sometimes is
                worse than no figure.
              */}
              <SubRow depth={depth + 1}>
                {count(loaded.length)} direct {loaded.length === 1 ? 'child' : 'children'} ·{' '}
                {money(round2(loaded.reduce((sum, child) => sum + child.balance, 0)), {
                  currency: loaded[0]?.currencyType,
                  alreadyDisplay: true,
                })}{' '}
                held between them ·{' '}
                {money(round2(loaded.reduce((sum, child) => sum + Math.abs(child.settlement), 0)), {
                  currency: loaded[0]?.currencyType,
                  alreadyDisplay: true,
                })}{' '}
                outstanding
              </SubRow>
            </>
          ) : null}
        </>
      ) : null}
    </>
  )
}

/** A row that is not an account — rollup, skeleton, error, or "no downline". */
function SubRow({
  depth,
  hidden,
  children,
}: {
  depth: number
  hidden?: boolean
  children: ReactNode
}) {
  return (
    <tr
      className="w7-dtree-sub"
      role="row"
      aria-level={depth + 1}
      aria-hidden={hidden ? 'true' : undefined}
      style={{ '--w7-depth': depth } as CSSProperties}
    >
      <td colSpan={COLUMNS}>
        <span className="w7-dtree-subline">{children}</span>
      </td>
    </tr>
  )
}
